import { useMutation, useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

interface IParams {
  page?: number;
  branch_id: number | undefined;
  name?: string;
  sku?: string;
  status?: string;
}

interface IRequestsParams {
  page?: number;
  name?: string;
  sku?: string;
  po_reference?: string;
  status?: string; //approved new | approved | cancelled
  supplier_id?: number;
}

interface IRequestBody {
  branch_id: number;
  items: number[];
}

export const useGetReordersList = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-reorders-list",
      params.page,
      params.branch_id,
      params.name,
      params.sku,
      params.status,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/reorders`, { params });
    },
    gcTime: 0,
    enabled: !!params.branch_id,
  });

export const useDownloadReorders = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "download-reorders-list",
      params.branch_id,
      params.name ?? "",
      params.sku,
      params.status,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/reorders/download`, { params });
    },
    enabled: false,
    gcTime: 0,
  });

// order requests
export const useGetReorderRequestsList = (params: IRequestsParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-reorder-requests-list",
      params.page,
      params.name,
      params.sku,
      params.status,
      params.po_reference,
      params.supplier_id,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/reorderRequests`, { params });
    },
    gcTime: 0,
  });

export const useDownloadReordersRequests = (params: IRequestsParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "download-reorders-requests",
      params.name,
      params.sku,
      params.status,
      params.po_reference,
      params.supplier_id,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/reorderRequests/download`, { params });
    },
    enabled: false,
    gcTime: 0,
  });

export const useAddReorderRequestMutation = () =>
  useMutation<void, APIErrorResponse, IRequestBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/reorderRequests`, data);
    },
  });

export const useUpdateReorderRequestItemMutation = () =>
  useMutation<
    void,
    APIErrorResponse,
    { id: number; supplier_id?: number; quantity?: number }
  >({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/reorderRequests/${id}`, rest);
    },
  });

export const useChangeReorderRequestsMutation = () =>
  useMutation<void, APIErrorResponse, { items: string[]; status: string }>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/reorderRequests/update-status`, data);
    },
  });

// po
interface IPoParams {
  page?: number;
  branch_id?: number | undefined;
  reference?: string;
  supplier_id?: number;
}

export const useGetPoRequests = (params: IPoParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-po-requests",
      params.page,
      params.branch_id,
      params.reference,
      params.supplier_id,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/poRequests`, { params });
    },
    gcTime: 0,
  });

export const useGetPoRequestById = (id: string) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-po-request-by-id", id],
    queryFn: async () => {
      return apiClient.get(`/api/poRequests/${id}`);
    },
  });

export const useCancelPoMutation = () =>
  useMutation<void, APIErrorResponse, { id: string | number }>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/poRequests/${data.id}/cancel`, data);
    },
  });

export const useSendPoMutation = () =>
  useMutation<
    void,
    APIErrorResponse,
    { id: string | number; send_method: "email" | "app" | "not_sent" }
  >({
    mutationFn: async (data) => {
      return apiClient.post(`/api/poRequests/${data.id}/send`, data);
    },
  });
